import React, { useContext, useState } from 'react'
import { ActionIcon, Button, Card, Code, Grid, Group, Notification, NotificationProps, Table, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import { doc, setDoc } from 'firebase/firestore';
import { ExternalGroup } from '../../types';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { useFirestore } from '../../helpers/firebaseContext';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Maintains config/externalGroups: directory groups from tenants the signed-in
 * admin cannot list through Graph (e.g. KFH bank). KFH supplies the group's
 * object id and display name; once saved here the group shows up in every
 * group picker and its id can be mapped to claims like any KFH Capital group.
 */
const ExternalGroupsSettings = () => {
    const configContext = useContext(ConfigContext);
    const appContext = useContext(MyUserDataContext);
    const db = useFirestore();
    const [notificationData, setNotificationData] = useState<null | NotificationProps>();
    const [saving, setSaving] = useState<boolean>(false);

    const groups: ExternalGroup[] = configContext.externalGroups?.groups ?? [];

    const form = useForm({
        initialValues: { id: '', displayName: '', tenant: 'KFH' },
        validate: {
            id: (value) => {
                const trimmed = value.trim().toLowerCase();
                if (!GUID_REGEX.test(trimmed)) return 'Enter the group object id (a GUID)';
                if (groups.some((group) => group.id.toLowerCase() === trimmed)) return 'This group is already listed';
                return null;
            },
            displayName: (value) => (value.trim().length > 0 ? null : 'Required'),
            tenant: (value) => (value.trim().length > 0 ? null : 'Required'),
        },
    });

    const save = async (nextGroups: ExternalGroup[], successMessage: string) => {
        setSaving(true);
        try {
            await setDoc(doc(db, "config", "externalGroups"), {
                groups: nextGroups,
                updatedBy: appContext.user?.email ?? appContext.msal?.account?.username ?? "",
            }, { merge: true });
            setNotificationData({ color: "green", children: <>{successMessage}</> });
        } catch (e) {
            console.error("ExternalGroupsSettings save failed", e);
            setNotificationData({ color: "red", children: <>Failed to save external groups</> });
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = form.onSubmit(async (values) => {
        const newGroup: ExternalGroup = {
            id: values.id.trim().toLowerCase(),
            displayName: values.displayName.trim(),
            tenant: values.tenant.trim(),
        };
        await save([...groups, newGroup], `Added ${newGroup.displayName}`);
        form.reset();
    });

    const handleRemove = async (group: ExternalGroup) => {
        await save(groups.filter((existing) => existing.id !== group.id), `Removed ${group.displayName}`);
    };

    return (
        <Card shadow="sm" p="xl" radius="md" withBorder mt="xl">
            <Text fw={700} mb="xs">External Groups</Text>
            <Text c="dimmed" size="sm" mb="lg">
                Groups from other Microsoft tenants (e.g. KFH) cannot be listed here automatically.
                Ask their admin for the group's object id and display name, add it below, and it will
                appear in the group pickers above so it can be mapped to permissions.
            </Text>

            {notificationData && <Notification mb="md" {...notificationData} onClose={() => setNotificationData(null)} />}

            {groups.length > 0 ? (
                <Table striped highlightOnHover withTableBorder mb="lg">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Tenant</Table.Th>
                            <Table.Th>Object id</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {groups.map((group) => (
                            <Table.Tr key={group.id}>
                                <Table.Td>{group.displayName}</Table.Td>
                                <Table.Td>{group.tenant}</Table.Td>
                                <Table.Td><Code>{group.id}</Code></Table.Td>
                                <Table.Td ta="right">
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        aria-label={`Remove ${group.displayName}`}
                                        disabled={saving}
                                        onClick={() => handleRemove(group)}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            ) : (
                <Text c="dimmed" size="sm" mb="lg">No external groups added yet.</Text>
            )}

            <form onSubmit={handleAdd}>
                <Grid align="flex-end">
                    <Grid.Col span={{ md: 5, xs: 12 }}>
                        <TextInput
                            label="Group object id"
                            placeholder="00000000-0000-0000-0000-000000000000"
                            {...form.getInputProps('id')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ md: 4, xs: 12 }}>
                        <TextInput label="Display name" placeholder="KFH Retail RMs" {...form.getInputProps('displayName')} />
                    </Grid.Col>
                    <Grid.Col span={{ md: 2, xs: 8 }}>
                        <TextInput label="Tenant" placeholder="KFH" {...form.getInputProps('tenant')} />
                    </Grid.Col>
                    <Grid.Col span={{ md: 1, xs: 4 }}>
                        <Group justify="flex-end">
                            <Button type="submit" loading={saving}>Add</Button>
                        </Group>
                    </Grid.Col>
                </Grid>
            </form>
        </Card>
    );
};

export default ExternalGroupsSettings
